/**
 * Части формы груза, общие для объявления и внутреннего заказа — блок `WEB-3.5`,
 * решение `Б4`: города, даты, груз, вес, ADR, комментарий, кузов и переподпись.
 *
 * Поле названо именем поля тела: тем же именем сервер называет поле в отказе, и
 * сообщение ложится под ним механизмом `renderFieldMessages`. Какие параметры кузова
 * применимы к типу, говорит справочник `listBodyTypes`: своей таблицы «тип →
 * параметры» у клиента нет, смена типа убирает поля прежнего, и их значения не
 * отправляются. Смена языка не стирает введённое (долг `TD-72`): поля остаются теми
 * же узлами, меняются только подписи.
 *
 * Ни цены, ни оплаты, ни условий здесь нет: они есть только у объявления и лежат в
 * его форме.
 */

import { ApiFailure } from '../api/client';
import type { ApiClient, CargoBodySubmission } from '../api/client';
import type { BodyTypeInfo, CargoBodyInput, CargoListing, City } from '../api/contract';
import { BODY_TYPES, LOADING_TYPES } from '../api/generated/enums';
import { bodyParameterStringKey, bodyTypeStringKey, loadingTypeStringKey } from '../i18n';
import type { Strings } from '../i18n';
import {
  createFieldMessageSlot,
  enumLabel,
  FIELD_MESSAGE_CLASS,
  refusalText,
  renderFieldMessages,
  selectEnumOption,
} from './state';

/** Поля параметров кузова в теле — всё, кроме типа и подтипа. */
type BodyFieldName = Exclude<keyof CargoBodyInput, 'type' | 'subtype'>;

/** Форма значения поля в теле: десятичная строка, целое или тип загрузки. */
type BodyFieldKind = 'decimal' | 'integer' | 'loading';

/**
 * Форма каждого поля параметров кузова — **схемой тела**, а не правилом:
 * размеры и тонны уходят десятичной строкой (двоичная дробь теряет знаки
 * молча, как у денег), температура и вместимость — целым, тип загрузки —
 * значением порождённого перечня. Какие поля показать, говорит справочник:
 * здесь только, каким элементом поле вводится. Запись полна по построению —
 * поле, добавленное в схему, ломает типы.
 */
const BODY_FIELD_KINDS: Readonly<Record<BodyFieldName, BodyFieldKind>> = {
  length_m: 'decimal',
  width_m: 'decimal',
  height_m: 'decimal',
  volume_m3: 'decimal',
  capacity_t: 'decimal',
  loading_type: 'loading',
  temperature_min_c: 'integer',
  temperature_max_c: 'integer',
  cars_capacity: 'integer',
};

const BODY_FIELD_NAMES = Object.keys(BODY_FIELD_KINDS) as BodyFieldName[];

/**
 * Поля тела, которыми справочник называет параметр. Имя параметра — имя поля;
 * параметр-пара (температура) называет **два** поля одним именем, и они
 * начинаются с него (так объявляет `CargoBodyParameter` контракта). Это
 * сопоставление имён, а не применимость: применимые параметры выбрал сервер.
 */
function fieldsOf(parameter: string): BodyFieldName[] {
  const found: BodyFieldName[] = [];

  for (const name of BODY_FIELD_NAMES) {
    if (name === parameter || name.startsWith(`${parameter}_`)) {
      found.push(name);
    }
  }

  return found;
}

/**
 * Имя поля кузова на форме — `body.<поле>`: так сервер называет поле
 * вложенного тела в отказе, и его сообщение ляжет под этим полем.
 */
function bodyFieldName(field: string): string {
  return `body.${field}`;
}

/** Значение порождённого перечня, совпавшее с выбранным, либо `null`. */
export function pick<T extends string>(values: readonly T[], chosen: string): T | null {
  for (const value of values) {
    if (value === chosen) {
      return value;
    }
  }

  return null;
}

/** Пустое поле — `null`; обязательность и пределы судит сервер. */
export function textOf(raw: string): string | null {
  return raw === '' ? null : raw;
}

export function decimalOf(raw: string): string | null {
  return textOf(raw.trim());
}

export function integerOf(raw: string): number | null {
  return raw.trim() === '' ? null : Number(raw);
}

/** Подпись города в подсказке: имя на языке ответа и страна под ним. */
function cityCaption(city: City): string {
  return `${city.name} (${city.country_name})`;
}

function cityIn(cities: readonly City[], id: string): City | null {
  for (const city of cities) {
    if (String(city.id) === id) {
      return city;
    }
  }

  return null;
}

/**
 * Отказ отправки на форме: сообщения сервера — под полями, которые он назвал;
 * непоказанное и общий отказ — над формой. Экран остаётся формой со всем введённым.
 */
export function showFormRefusal(form: HTMLElement, notice: HTMLElement, error: unknown, strings: Strings): void {
  const failure = error instanceof ApiFailure ? error : null;
  const homeless = renderFieldMessages(form, failure?.details ?? []);
  const texts: string[] = [];

  for (const detail of homeless) {
    texts.push(detail.message);
  }

  if (texts.length === 0) {
    texts.push(refusalText(error, strings));
  }

  notice.textContent = texts.join(' ');
}

export interface CityField {
  readonly select: HTMLSelectElement;
  readonly label: HTMLLabelElement;
}

export interface CargoFormParts {
  /** Подпись, которая ставится сейчас и при каждой смене языка. */
  caption(apply: () => void): void;
  labelled(control: HTMLElement, text: () => string): HTMLLabelElement;
  input(name: string, type: string): HTMLInputElement;
  checkbox(name: string, text: () => string): { label: HTMLLabelElement; toggle: HTMLInputElement };
  /** Выбор из порождённого перечня: значения — контракта, подписи — строителем. */
  enumSelect(name: string, values: readonly string[], keyOf: (value: string) => keyof Strings): HTMLSelectElement;
  readonly origin: CityField;
  readonly destination: CityField;
  readonly loadingAt: HTMLInputElement;
  readonly unloadBy: HTMLInputElement;
  readonly cargoType: HTMLInputElement;
  readonly weight: HTMLInputElement;
  readonly adr: HTMLInputElement;
  readonly comment: HTMLTextAreaElement;
  readonly loadingAtField: HTMLLabelElement;
  readonly unloadByField: HTMLLabelElement;
  readonly cargoTypeField: HTMLLabelElement;
  readonly weightField: HTMLLabelElement;
  readonly adrField: HTMLLabelElement;
  readonly bodyTypeField: HTMLLabelElement;
  readonly bodyArea: HTMLElement;
  readonly commentField: HTMLLabelElement;
  /** Ответ справочника кузовов: варианты типа и поля выбранного. */
  setBodyTypes(types: readonly BodyTypeInfo[]): void;
  /** Выбранный город из ответа сервера. */
  chooseCity(field: CityField, city: City): void;
  /** Кузов объявления из ответа сервера. */
  fillBody(body: CargoListing['body']): void;
  enteredBody(): CargoBodySubmission;
  /** Подписи на новом языке; поля и введённое остаются (долг `TD-72`). */
  relabel(next: Strings): void;
}

interface CityState extends CityField {
  readonly search: HTMLInputElement;
  /** Выбранный город — остаётся вариантом и тогда, когда новый поиск его не вернул. */
  chosen: City | null;
  shown: readonly City[];
  /** Поколение поиска: ответ на устаревшую строку к выбору не применяется. */
  generation: number;
}

export function createCargoFormParts(options: {
  readonly client: ApiClient;
  readonly strings: Strings;
  /** Подпись веса: у объявления вес на одну машину, у заказа числа машин нет. */
  readonly weightCaption: (strings: Strings) => string;
}): CargoFormParts {
  const { client } = options;
  let strings = options.strings;

  /**
   * Подписи формы: каждая ставится одной записью отсюда — и при сборке, и при
   * смене языка. Поля и введённое в них этим не трогаются (долг `TD-72`).
   */
  const captions: Array<() => void> = [];

  function caption(apply: () => void): void {
    captions.push(apply);
    apply();
  }

  function labelledWith(control: HTMLElement, text: () => string, extra: readonly HTMLElement[]): HTMLLabelElement {
    const label = document.createElement('label');
    const shown = document.createElement('span');

    label.className = 'form-field';
    shown.className = 'form-field__caption';
    caption(() => {
      shown.textContent = text();
    });
    label.append(shown, ...extra, control, createFieldMessageSlot());

    return label;
  }

  function labelled(control: HTMLElement, text: () => string): HTMLLabelElement {
    return labelledWith(control, text, []);
  }

  function input(name: string, type: string): HTMLInputElement {
    const node = document.createElement('input');

    node.name = name;
    node.type = type;

    return node;
  }

  function checkbox(name: string, text: () => string): { label: HTMLLabelElement; toggle: HTMLInputElement } {
    const toggle = input(name, 'checkbox');
    const label = labelled(toggle, text);

    label.classList.add('form-field--check');

    return { label, toggle };
  }

  function enumSelect(name: string, values: readonly string[], keyOf: (value: string) => keyof Strings): HTMLSelectElement {
    const select = document.createElement('select');

    select.name = name;
    select.append(document.createElement('option'));

    for (const value of values) {
      const choice = document.createElement('option');

      choice.value = value;
      caption(() => {
        choice.textContent = enumLabel(strings, keyOf(value));
      });
      select.append(choice);
    }

    return select;
  }

  // ── Город: подсказка справочника по строке поиска ───────────────────────

  function cityField(name: string, text: () => string): CityState {
    const select = document.createElement('select');
    // Строка поиска имени не несёт: в тело она не уходит, и сообщению сервера
    // ложиться под неё незачем — его поле назовёт выбор города.
    const search = document.createElement('input');

    select.name = name;
    search.type = 'search';
    caption(() => {
      search.placeholder = strings.cargo_listing_form_city_search;
    });

    const field: CityState = {
      select,
      search,
      label: labelledWith(select, text, [search]),
      chosen: null,
      shown: [],
      generation: 0,
    };

    search.addEventListener('input', () => {
      void suggest(field);
    });
    select.addEventListener('change', () => {
      field.chosen = cityIn(field.shown, select.value);
    });
    fillCities(field, []);

    return field;
  }

  /** Варианты — ответ справочника в его порядке; выбранный город не теряется. */
  function fillCities(field: CityState, found: readonly City[]): void {
    const cities: City[] = [];
    const chosen = field.chosen;

    if (chosen !== null && cityIn(found, String(chosen.id)) === null) {
      cities.push(chosen);
    }

    cities.push(...found);
    field.shown = cities;

    const choices: HTMLOptionElement[] = [document.createElement('option')];

    for (const city of cities) {
      const choice = document.createElement('option');

      choice.value = String(city.id);
      choice.textContent = cityCaption(city);
      choices.push(choice);
    }

    field.select.replaceChildren(...choices);
    field.select.value = chosen === null ? '' : String(chosen.id);
    // Блок `WEB-D.2`, решение `Б4`: пока выбирать нечего, на экране один ввод поиска.
    field.select.hidden = cities.length === 0;
  }

  async function suggest(field: CityState): Promise<void> {
    const query = field.search.value;

    if (query.trim() === '') {
      return;
    }

    field.generation += 1;

    const mine = field.generation;
    const slot = field.label.querySelector(`.${FIELD_MESSAGE_CLASS}`);

    try {
      const found = await client.listCities({ q: query });

      if (mine !== field.generation) {
        return;
      }

      if (slot !== null) {
        slot.textContent = '';
      }

      fillCities(field, found);
    } catch (error) {
      // Годность строки поиска судит сервер; его отказ — текстом под полем,
      // прежние варианты остаются.
      if (mine === field.generation && slot !== null) {
        slot.textContent = refusalText(error, strings);
      }
    }
  }

  const origin = cityField('origin_city_id', () => strings.cargo_listing_form_origin);
  const destination = cityField('destination_city_id', () => strings.cargo_listing_form_destination);
  const loadingAt = input('loading_at', 'date');
  const unloadBy = input('unload_by', 'date');
  const cargoType = input('cargo_type', 'text');
  const weight = input('weight_kg', 'number');
  const adr = checkbox('adr', () => strings.cargo_listing_form_adr);
  const bodyType = document.createElement('select');
  const bodyArea = document.createElement('div');
  const comment = document.createElement('textarea');

  bodyArea.className = 'form';
  bodyType.name = bodyFieldName('type');
  comment.name = 'comment';

  // ── Кузов: справочник сервера ───────────────────────────────────────────

  let bodyTypes: readonly BodyTypeInfo[] = [];
  let subtypeSelect: HTMLSelectElement | null = null;
  /** Поля параметров, показанные сейчас, — только они уходят значением. */
  const parameterFields = new Map<BodyFieldName, HTMLInputElement | HTMLSelectElement>();
  /** Введённое в параметры — переживает смену типа у полей, применимых к обоим. */
  const parameterValues = new Map<BodyFieldName, string>();
  /** Подписи полей кузова: пересобираются с полями при смене типа. */
  let bodyCaptions: Array<() => void> = [];

  function chosenBodyType(): BodyTypeInfo | null {
    for (const info of bodyTypes) {
      if (info.type === bodyType.value) {
        return info;
      }
    }

    return null;
  }

  function fillBodyTypes(): void {
    const choices: HTMLOptionElement[] = [document.createElement('option')];

    // Порядок и имена — ответа справочника, на языке ответа (поправка
    // CONTRACT `К4`).
    for (const { type, name, typical } of bodyTypes) {
      const choice = document.createElement('option');

      choice.value = type;
      choice.textContent = `${name} (${typical})`;
      choices.push(choice);
    }

    bodyType.replaceChildren(...choices);
  }

  function bodyLabel(control: HTMLElement, text: () => string): HTMLLabelElement {
    const label = document.createElement('label');
    const shown = document.createElement('span');
    const apply = (): void => {
      shown.textContent = text();
    };

    label.className = 'form-field';
    shown.className = 'form-field__caption';
    bodyCaptions.push(apply);
    apply();
    label.append(shown, control, createFieldMessageSlot());

    return label;
  }

  function parameterControl(name: BodyFieldName): HTMLInputElement | HTMLSelectElement {
    const fieldName = bodyFieldName(name);

    switch (BODY_FIELD_KINDS[name]) {
      case 'loading': {
        const select = document.createElement('select');

        select.name = fieldName;
        select.append(document.createElement('option'));

        for (const value of LOADING_TYPES) {
          const choice = document.createElement('option');

          choice.value = value;
          choice.textContent = enumLabel(strings, loadingTypeStringKey(value));
          bodyCaptions.push(() => {
            choice.textContent = enumLabel(strings, loadingTypeStringKey(value));
          });
          select.append(choice);
        }

        return select;
      }

      case 'integer':
        return input(fieldName, 'number');

      case 'decimal': {
        const node = input(fieldName, 'text');

        node.inputMode = 'decimal';

        return node;
      }
    }
  }

  /**
   * Поля кузова выбранного типа — **ровно** параметры справочника, в его
   * порядке. Поле прежнего типа, неприменимое к новому, исчезает, и его
   * значение не уходит; подтип принадлежит типу и при смене типа снимается.
   */
  function rebuildBody(): void {
    const info = chosenBodyType();
    const parts: HTMLElement[] = [];

    parameterFields.clear();
    bodyCaptions = [];
    subtypeSelect = null;

    if (info !== null && info.subtypes.length > 0) {
      const select = document.createElement('select');

      select.name = bodyFieldName('subtype');
      select.append(document.createElement('option'));

      for (const subtype of info.subtypes) {
        const choice = document.createElement('option');

        choice.value = subtype.value;
        choice.textContent = subtype.name;
        select.append(choice);
      }

      subtypeSelect = select;
      parts.push(bodyLabel(select, () => strings.cargo_listing_form_body_subtype));
    }

    for (const parameter of info === null ? [] : info.parameters) {
      const names = fieldsOf(parameter);

      names.forEach((name, index) => {
        const control = parameterControl(name);

        control.value = parameterValues.get(name) ?? '';
        control.addEventListener('input', () => {
          parameterValues.set(name, control.value);
        });
        control.addEventListener('change', () => {
          parameterValues.set(name, control.value);
        });
        parameterFields.set(name, control);
        // Параметр-пара подписан концами: «от» и «до» по порядку полей схемы.
        parts.push(
          bodyLabel(control, () => {
            const shown = enumLabel(strings, bodyParameterStringKey(parameter));

            if (names.length < 2) {
              return shown;
            }

            const ends = [strings.cargo_listing_form_temperature_from, strings.cargo_listing_form_temperature_to];

            return `${shown} ${ends[index] ?? ''}`.trim();
          }),
        );
      });
    }

    bodyArea.replaceChildren(...parts);
  }

  bodyType.addEventListener('change', () => {
    rebuildBody();
  });

  /** Значение поля кузова формой схемы; непоказанное поле уходит `null`. */
  function bodyValue(name: BodyFieldName): string | number | null {
    const control = parameterFields.get(name);

    if (control === undefined) {
      return null;
    }

    switch (BODY_FIELD_KINDS[name]) {
      case 'decimal':
        return decimalOf(control.value);
      case 'integer':
        return integerOf(control.value);
      case 'loading':
        return pick(LOADING_TYPES, control.value);
    }
  }

  function chosenSubtype(): CargoBodySubmission['subtype'] {
    const info = chosenBodyType();

    if (info === null || subtypeSelect === null) {
      return null;
    }

    for (const subtype of info.subtypes) {
      if (subtype.value === subtypeSelect.value) {
        return subtype.value;
      }
    }

    return null;
  }

  function enteredBody(): CargoBodySubmission {
    const parameters: Partial<Record<BodyFieldName, string | number | null>> = {};

    for (const name of BODY_FIELD_NAMES) {
      parameters[name] = bodyValue(name);
    }

    // Приведение названо: каждое поле записи `BODY_FIELD_KINDS` получило
    // значение своей формы (десятичная строка, целое, тип загрузки), а полноту
    // записи держит её тип — язык типов не выводит этого из обхода ключей.
    return {
      type: pick(BODY_TYPES, bodyType.value),
      subtype: chosenSubtype(),
      ...parameters,
    } as CargoBodySubmission;
  }

  function fillBody(body: CargoListing['body']): void {
    selectEnumOption(bodyType, bodyTypeStringKey(body.type), bodyTypeStringKey);

    for (const name of BODY_FIELD_NAMES) {
      const value = body[name];

      if (value !== null) {
        parameterValues.set(name, String(value));
      }
    }

    rebuildBody();

    if (subtypeSelect !== null) {
      subtypeSelect.value = body.subtype ?? '';
    }
  }

  return {
    caption,
    labelled,
    input,
    checkbox,
    enumSelect,
    origin,
    destination,
    loadingAt,
    unloadBy,
    cargoType,
    weight,
    adr: adr.toggle,
    comment,
    loadingAtField: labelled(loadingAt, () => strings.cargo_listing_form_loading_at),
    unloadByField: labelled(unloadBy, () => strings.cargo_listing_form_unload_by),
    cargoTypeField: labelled(cargoType, () => strings.cargo_listing_form_cargo_type),
    weightField: labelled(weight, () => options.weightCaption(strings)),
    adrField: adr.label,
    bodyTypeField: labelled(bodyType, () => strings.cargo_listing_form_body_type),
    bodyArea,
    commentField: labelled(comment, () => strings.cargo_listing_form_comment),

    setBodyTypes(types: readonly BodyTypeInfo[]): void {
      bodyTypes = types;
      fillBodyTypes();
      rebuildBody();
    },

    chooseCity(field: CityField, city: City): void {
      const state = field as CityState;

      state.chosen = city;
      fillCities(state, []);
    },

    fillBody,
    enteredBody,

    relabel(next: Strings): void {
      strings = next;

      for (const apply of captions) {
        apply();
      }

      for (const apply of bodyCaptions) {
        apply();
      }
    },
  };
}
